import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';

const RecentLeaveHistory = () => {
  const authContext = useAuth();
  const user = authContext?.user;
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDaysUsed, setTotalDaysUsed] = useState(0);

  useEffect(() => {
    if (user?.id) {
      loadLeaveHistory();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadLeaveHistory = async () => {
    try {
      setLoading(true);
      
      // Fetch recent leave requests from Supabase
      const { data: leaves, error } = await supabase
        ?.from('leave_requests')
        ?.select('id, leave_type, start_date, end_date, days_requested, status, created_at, reason')
        ?.eq('employee_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(4);

      if (error) throw error;

      // Transform data from snake_case to camelCase
      const transformedLeaves = leaves?.map(leave => ({
        id: leave?.id,
        type: leave?.leave_type,
        startDate: leave?.start_date,
        endDate: leave?.end_date,
        days: leave?.days_requested,
        status: leave?.status,
        appliedDate: leave?.created_at,
        reason: leave?.reason || 'No reason provided'
      })) || [];

      setLeaveHistory(transformedLeaves);

      // Calculate total days used this year (approved leaves only)
      const currentYear = new Date()?.getFullYear();
      const { data: approvedLeaves, error: totalError } = await supabase
        ?.from('leave_requests')
        ?.select('days_requested')
        ?.eq('employee_id', user?.id)
        ?.eq('status', 'approved')
        ?.gte('start_date', `${currentYear}-01-01`)
        ?.lte('start_date', `${currentYear}-12-31`);

      if (!totalError && approvedLeaves) {
        const total = approvedLeaves?.reduce((sum, leave) => sum + (leave?.days_requested || 0), 0);
        setTotalDaysUsed(total);
      }
    } catch (err) {
      console.error('Failed to load leave history:', err);
      setLeaveHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'rejected':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getLeaveTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'annual leave':
        return 'Calendar';
      case 'sick leave':
        return 'Heart';
      case 'personal leave':
        return 'User';
      default:
        return 'Calendar';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border card-shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Recent Leave History</h3>
        <Button variant="ghost" size="sm">
          View All
          <Icon name="ArrowRight" size={14} className="ml-1" />
        </Button>
      </div>
      
      {leaveHistory?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">No leave history yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {leaveHistory?.map((leave) => (
            <div key={leave?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon 
                    name={getLeaveTypeIcon(leave?.type)} 
                    size={18} 
                    color="var(--color-primary)" 
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-foreground">{leave?.type}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(leave?.status)}`}>
                      {leave?.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="Calendar" size={14} />
                      <span>
                        {formatDate(leave?.startDate)} - {formatDate(leave?.endDate)}
                      </span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={14} />
                      <span>{leave?.days} day{leave?.days > 1 ? 's' : ''}</span>
                    </span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">
                    {leave?.reason}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-xs text-muted-foreground">Applied</p>
                <p className="text-sm font-medium text-foreground">
                  {formatDate(leave?.appliedDate)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Total leaves this year:</span>
          <span className="font-medium text-foreground">{totalDaysUsed} days used</span>
        </div>
      </div>
    </div>
  );
};

export default RecentLeaveHistory;